import LogRocket from 'logrocket'
import createPlugin from 'logrocket-vuex'

const LOGROCKET_MAX_STRING_LENGTH = 500
const LOGROCKET_MAX_OBJECT_KEYS = 20
const LOGROCKET_MAX_ARRAY_ITEMS = 5
const LOGROCKET_MAX_DEPTH = 2

type VuexMutation = {
  type: string
  payload?: unknown
}

export function sanitizeLogRocketMutation(mutation: VuexMutation) {
  return {
    type: mutation.type,
    payload: summarizeForLogRocket(mutation.payload),
  }
}

function summarizeForLogRocket(value: unknown, depth = 0, seen = new WeakSet<object>()): unknown {
  if (value == null || typeof value === 'number' || typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'string') {
    return {
      payloadType: 'string',
      length: value.length,
      truncated: value.length > LOGROCKET_MAX_STRING_LENGTH,
    }
  }

  if (typeof value === 'function') {
    return '[Function]'
  }

  if (typeof value === 'bigint' || typeof value === 'symbol') {
    return value.toString()
  }

  if (typeof value !== 'object') {
    return `[${typeof value}]`
  }

  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return {
      payloadType: value.constructor.name,
      size: value.size,
      mimeType: value.type,
    }
  }

  if (seen.has(value)) {
    return '[Circular]'
  }
  seen.add(value)

  if (Array.isArray(value)) {
    const summary: Record<string, unknown> = {
      payloadType: 'array',
      length: value.length,
    }

    if (depth < LOGROCKET_MAX_DEPTH) {
      summary.sample = value
        .slice(0, LOGROCKET_MAX_ARRAY_ITEMS)
        .map((item) => summarizeForLogRocket(item, depth + 1, seen))
    }

    return summary
  }

  const objectValue = value as Record<string, unknown>
  const keys = Object.keys(objectValue)
  if (depth >= LOGROCKET_MAX_DEPTH) {
    return {
      payloadType: Object.prototype.toString.call(value).slice(8, -1),
      keyCount: keys.length,
    }
  }

  return {
    payloadType: Object.prototype.toString.call(value).slice(8, -1),
    keyCount: keys.length,
    keys: keys
      .slice(0, LOGROCKET_MAX_OBJECT_KEYS)
      .reduce<Record<string, unknown>>((summary, key) => {
        summary[key] = summarizeForLogRocket(objectValue[key], depth + 1, seen)
        return summary
      }, {}),
    truncatedKeyCount: Math.max(keys.length - LOGROCKET_MAX_OBJECT_KEYS, 0),
  }
}

export const vuexLogRocketPlugin = createPlugin(LogRocket, sanitizeLogRocketMutation)
